import { Formik, Form } from 'formik'
import { Fragment, useEffect, useState } from 'react'
import Column from '@components/column'
import Typography from '@components/typography'
import Fields from '@components/fields'
import { Constant } from '@utils'
import { useSelector } from 'react-redux'
import { Availability } from '@apis'
import { Actions, store } from '../../store'
import Button from '../../components/button'
import { AvailabilitySchema } from '../../validations'
const today = new Date()
const weekday = today.toLocaleDateString('en-US', { weekday: 'long' })
const initial = {
  appointmentType: '',
  day: weekday,
  id: null,
  isAlwaysOnline: false,
  slots: {
    morning: [],
    afternoon: [],
    evening: [],
  },
  availabilities: [
    {
      day: 'Sunday',
      slots: {
        morning: [],
        afternoon: [],
        evening: [],
      },
    },
    {
      day: 'Monday',
      slots: {
        morning: [],
        afternoon: [],
        evening: [],
      },
    },
    {
      day: 'Tuesday',
      slots: {
        morning: [],
        afternoon: [],
        evening: [],
      },
    },
    {
      day: 'Wednesday',
      slots: {
        morning: [],
        afternoon: [],
        evening: [],
      },
    },
    {
      day: 'Thursday',
      slots: {
        morning: [],
        afternoon: [],
        evening: [],
      },
    },
    {
      day: 'Friday',
      slots: {
        morning: [],
        afternoon: [],
        evening: [],
      },
    },
    {
      day: 'Saturday',
      isAlwaysOnline: false,
      slots: {
        morning: [],
        afternoon: [],
        evening: [],
      },
    },
  ],
}
const Availabilities = ({ userId, appointmentType, disabled = false }) => {
  const [loading, setLoading] = useState<boolean>(false)
  const [initialValues, setInitialValues] = useState(initial)
  const { data: availabilities } = useSelector(
    (state: any) => state.availabilities,
  )
  const formatSlots = (slots) => {
    if (!!slots) {
      const formattedSlots = Object.entries(slots)?.reduce(
        (acc, [key, items]: any) => {
          acc[key] = items?.map(
            (item: any) => `${item?.startTime}-${item?.endTime}`,
          )
          return acc
        },
        {},
      )
      return formattedSlots
    } else {
      return null
    }
  }

  const submitHandler = async (values) => {
    setLoading(true)
    const payload: any = {
      day: values?.day,
      isAlwaysOnline: values?.isAlwaysOnline,
      slots: {},
    }

    Object.entries(values?.slots).flatMap(([shift, times]: any) =>
      times.map((time, index) => {
        const [startTime, endTime] = time.split('-')
        const item = {
          interval: 30,
          startTime,
          endTime,
          index,
        }
        payload.slots[shift] = [...(payload.slots[shift] || []), item]
      }),
    )

    if (!!values?.id) {
      await Availability.update({
        id: values?.id,
        data: payload,
      }).catch(() => {})
    } else {
      await Availability.create({
        data: payload,
      })
        .then((resp) => {
          const findIndex = Constant.weekDays?.findIndex(
            (day: any) => day?.label == resp?.day,
          )
          const updatedAvailabilities = [...availabilities]
          updatedAvailabilities[findIndex] = resp
          store.dispatch(Actions.set('availabilities', updatedAvailabilities))
        })
        .catch(() => {})
    }

    setLoading(false)
  }

  const fetchData = async () => {
    await Availability.list({
      params: {
        userId,
      },
    }).catch(() => {})
  }

  useEffect(() => {
    if (!availabilities?.length && !!userId) {
      fetchData()
    }
    return () => {
      store.dispatch(Actions.reset('availabilitie'))
    }
  }, [userId])

  useEffect(() => {
    if (availabilities?.length) {
      const selectedWeekDay = availabilities?.find(
        (availability: any) => availability?.day == weekday,
      )
      const slots: any = formatSlots(selectedWeekDay?.slots)
      setInitialValues({
        ...initial,
        appointmentType: appointmentType,
        isAlwaysOnline: selectedWeekDay?.isAlwaysOnline,
        slots: slots
          ? {
              morning: slots?.morning || [],
              afternoon: slots?.afternoon || [],
              evening: slots?.evening || [],
            }
          : initial?.slots,
      })
    } else {
      setInitialValues(initial)
    }
  }, [availabilities?.length])

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={submitHandler}
      enableReinitialize
      validationSchema={AvailabilitySchema.validationSchema}
    >
      {({ handleSubmit, setFieldValue, values }) => {
        return (
          <Form onSubmit={handleSubmit}>
            <Column rowGap={20}>
              <Column bg="white" styleColumn="padding:20px;border-radius:8px">
                <Typography content="Appointment Types" tc="text" size={14} />
                <Fields.ChooseButton
                  name="appointmentType"
                  items={[
                    {
                      label: 'Online Appointment',
                      value: 'Online Appointment',
                    },
                    {
                      label: 'Only Clinic',
                      value: 'Only Clinic',
                    },
                    {
                      label: 'Online & Clinic',
                      value: 'Both',
                    },
                  ]}
                  titleAsKey="label"
                  valueAsKey="value"
                  styleWrapper="margin-top:20px"
                  multiple={false}
                  disabled={true}
                />
              </Column>
              <Column bg="white" styleColumn="padding:20px;border-radius:8px;">
                <Typography content="Weekdays" tc="text" size={14} />
                <Fields.ChooseButton
                  name="day"
                  items={Constant.weekDays}
                  titleAsKey="label"
                  valueAsKey="label"
                  styleWrapper="margin-top:20px"
                  styleButton="height:70px;"
                  multiple={false}
                  onChange={(value) => {
                    setFieldValue('day', value)
                    const selectedWeekDay = availabilities?.find(
                      (availability: any) => availability?.day == value,
                    )
                    const slots: any = formatSlots(selectedWeekDay?.slots)
                    setFieldValue('id', selectedWeekDay?.id || null)
                    setFieldValue(
                      'isAlwaysOnline',
                      selectedWeekDay?.isAlwaysOnline || false,
                    )
                    setFieldValue(
                      'slots',
                      !!slots
                        ? {
                            morning: slots?.morning || [],
                            afternoon: slots?.afternoon || [],
                            evening: slots?.evening || [],
                          }
                        : initial?.slots,
                    )
                  }}
                />
              </Column>

              <Fields.Switch
                name="isAlwaysOnline"
                label="Is Available"
                disabled={disabled}
              />

              {Object.entries(Constant.slots)?.map(([key, items]: any) => {
                return (
                  <Fragment key={key}>
                    <Column
                      bg="white"
                      styleColumn="padding:20px;border-radius:8px;"
                    >
                      <Typography
                        content={`${key} Slots`}
                        tc="text"
                        size={14}
                        styleText="text-transform:capitalize;"
                      />
                      <Fields.ChooseButton
                        name={`slots[${key}]`}
                        items={items?.map((item: any) => {
                          return {
                            label: `${item?.startTime}-${item?.endTime}`,
                            value: `${item?.startTime}-${item?.endTime}`,
                          }
                        })}
                        titleAsKey="label"
                        valueAsKey="value"
                        styleWrapper="margin-top:20px"
                        disabled={disabled}
                      />
                    </Column>
                  </Fragment>
                )
              })}
              {!disabled && (
                <Column alignItems="flex-end">
                  <Button
                    title="Update"
                    height={45}
                    type="submit"
                    loading={loading}
                  />
                </Column>
              )}
            </Column>
          </Form>
        )
      }}
    </Formik>
  )
}

export default Availabilities
